/**
 * Offline copies of files a driver may need with no signal (e.g. the
 * insurance card for their favorite trucks at a roadside inspection).
 * Stored with the browser's Cache Storage, keyed by storage path; works in
 * the Capacitor WebView too. Best-effort: if the cache isn't available the
 * app simply fetches online.
 */
import { useEffect, useState } from 'react';
import { getDb } from '@routeback/core';
import { supabase } from './supabase';

const CACHE = 'routeback-files-v1';
const key = (bucket: string, path: string) => `/__files/${bucket}/${path}`;

async function open(): Promise<Cache | null> {
  try {
    return typeof caches === 'undefined' ? null : await caches.open(CACHE);
  } catch {
    return null;
  }
}

/** Save a copy of each file not already saved. Returns how many are now on the phone. */
export async function keepOffline(bucket: string, paths: string[]): Promise<number> {
  const c = await open();
  if (!c || !supabase) return 0;
  let n = 0;
  for (const p of paths) {
    if (!p) continue;
    if (await c.match(key(bucket, p))) { n++; continue; }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
    try {
      const { data } = await supabase.storage.from(bucket).createSignedUrl(p, 300);
      if (!data) continue;
      const res = await fetch(data.signedUrl);
      if (!res.ok) continue;
      await c.put(key(bucket, p), res);
      n++;
    } catch {
      /* try again next time */
    }
  }
  return n;
}

/** Drop saved copies that are no longer wanted (a truck un-favorited, a document replaced). */
export async function pruneOffline(bucket: string, keep: string[]): Promise<void> {
  const c = await open();
  if (!c) return;
  const wanted = new Set(keep.map((p) => key(bucket, p)));
  for (const req of await c.keys()) {
    const path = new URL(req.url).pathname;
    if (path.startsWith(`/__files/${bucket}/`) && !wanted.has(decodeURI(path))) await c.delete(req);
  }
}

/**
 * A URL to show or open a file: the copy still waiting to upload (this
 * device), the saved offline copy, or a short-lived signed link.
 */
export function useFileUrl(bucket: string, path: string | null): { url: string | null; offline: boolean } {
  const [state, setState] = useState<{ url: string | null; offline: boolean }>({ url: null, offline: false });
  useEffect(() => {
    if (!path) { setState({ url: null, offline: false }); return; }
    let alive = true;
    let blobUrl: string | null = null;
    void (async () => {
      const pending = await getDb().pending_uploads.get(path);
      if (pending) { blobUrl = URL.createObjectURL(pending.blob); if (alive) setState({ url: blobUrl, offline: true }); return; }
      const c = await open();
      const hit = c ? await c.match(key(bucket, path)) : undefined;
      if (hit) { blobUrl = URL.createObjectURL(await hit.blob()); if (alive) setState({ url: blobUrl, offline: true }); return; }
      if (!supabase) return;
      const { data } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (alive && data) setState({ url: data.signedUrl, offline: false });
    })();
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [bucket, path]);
  return state;
}
